// client/src/components/ProfilePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Box, Tabs, Tab, Typography, TextField, Button, Avatar,
  IconButton, Alert, Snackbar, FormControlLabel, Checkbox, Select,
  MenuItem, FormControl, InputLabel, Grid, Paper, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButton, ToggleButtonGroup, Tooltip,
  Accordion, AccordionSummary, AccordionDetails, Slider, Chip, Divider,
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../axiosInstance';
import AppBarHeader from './AppBarHeader';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { THEMES, THEME_LABELS } from '../constants/themes';
import { FONT_CATEGORIES } from '../constants/fonts';
import AppIcon from './AppIcon';
import ActivityTab from './activity/ActivityTab';

const TAB_LABELS = ['profile', 'account', 'security', 'privacy', 'appearance', 'data', 'schemas', 'activity'];

const COLLECTIONS = [
  { key: 'games',         label: 'Games',         hiddenKey: 'Games Collection',         featureFlag: 'enableGamesFeature' },
  { key: 'consoles',      label: 'Consoles',       hiddenKey: 'Console Collection',       featureFlag: 'enableConsolesFeature' },
  { key: 'lego',          label: 'LEGO',           hiddenKey: 'LEGO Collection',          featureFlag: 'enableLegoFeature' },
  { key: 'movies',        label: 'Movies',         hiddenKey: 'Movie Collection',         featureFlag: 'enableMoviesFeature' },
  { key: 'music',         label: 'Music',          hiddenKey: 'Music Collection',         featureFlag: 'enableMusicFeature' },
  { key: 'books',         label: 'Books',          hiddenKey: 'Book Collection',          featureFlag: 'enableBooksFeature' },
  { key: 'board-games',   label: 'Board Games',    hiddenKey: 'Board Game Collection',    featureFlag: 'enableBoardGamesFeature' },
  { key: 'trading-cards', label: 'Trading Cards',  hiddenKey: 'Trading Cards Collection', featureFlag: 'enableTradingCardsFeature' },
  { key: 'manga',         label: 'Manga',          hiddenKey: 'Manga Collection',         featureFlag: 'enableMangaFeature' },
  { key: 'comics',        label: 'Comics',         hiddenKey: 'Comics Collection',        featureFlag: 'enableComicsFeature' },
];

const VIEW_MODE_OPTIONS = [
  { value: 'grid',     label: 'Grid' },
  { value: 'list',     label: 'List' },
  { value: 'table',    label: 'Table' },
  { value: 'detailed', label: 'Detailed' },
  { value: 'large',    label: 'Large' },
  { value: 'shelf',    label: 'Shelf' },
];

const SORT_OPTIONS = [
  { value: 'alpha_asc',        label: 'Alphabetical (A→Z)' },
  { value: 'alpha_desc',       label: 'Alphabetical (Z→A)' },
  { value: 'date_desc',        label: 'Date Added (Newest)' },
  { value: 'date_asc',         label: 'Date Added (Oldest)' },
  { value: 'year_desc',        label: 'Release Year (Newest)' },
  { value: 'year_asc',         label: 'Release Year (Oldest)' },
  { value: 'rating_desc',      label: 'Rating (High→Low)' },
  { value: 'rating_asc',       label: 'Rating (Low→High)' },
  { value: 'favourites_first', label: 'Favourites First' },
];

const lsViewKey = (c) => `${c}CollectionViewMode`;
const lsSortKey = (c) => `${c}CollectionSortOrder`;

const CURATED_ICONS = [
  'SportsEsports','VideogameAsset','Casino','Gamepad','Album','MusicNote','LibraryMusic',
  'Headphones','Movie','Tv','LocalMovies','LiveTv','MenuBook','AutoStories','LocalLibrary',
  'Book','Extension','Toys','Build','Train','DirectionsCar','Flight','Sailing',
  'Star','Favorite','EmojiEvents','WorkspacePremium','Bolt','LocalFireDepartment',
  'Pets','Nature','Park','BeachAccess','Terrain','WbSunny',
  'Camera','Photo','ColorLens','Palette','Brush','Create','Style',
  'Sports','SportsSoccer','SportsBasketball','SportsFootball','SportsTennis',
  'Computer','Smartphone','TabletMac','Watch','Headset','Speaker','Radio',
  'ShoppingBag','ShoppingCart','Store','LocalMall','Redeem','CardGiftcard',
  'Restaurant','LocalCafe','LocalBar','Kitchen',
  'AccountBalance','AttachMoney','Savings',
  'Home','Apartment','Weekend','Chair',
  'Science','Biotech','Psychology','School','Engineering',
];

function BackgroundSubSection({ label, value, onSave, saving, onUpload, uploading }) {
  const [kind, setKind] = useState(value?.kind || 'none');
  const [color, setColor] = useState(value?.color || '#1a1a2e');
  const [color2, setColor2] = useState(value?.color2 || '#2d1b69');
  const [angle, setAngle] = useState(value?.angle ?? 135);

  const buildValue = () => ({ kind, color, color2, angle });
  const inputId = `bg-upload-${label.replace(/\s/g, '')}`;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{label}</Typography>
      <ToggleButtonGroup
        size="small" exclusive value={kind}
        onChange={(_, v) => { if (v) setKind(v); }}
        sx={{ mb: 2 }}
      >
        {['none', 'solid', 'gradient', 'image'].map(opt => (
          <ToggleButton key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {kind === 'solid' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <input type="color" value={color} onChange={e => setColor(e.target.value)}
            style={{ width: 48, height: 40, border: 'none', cursor: 'pointer', borderRadius: 8 }} />
          <Typography variant="body2" color="text.secondary">{color}</Typography>
        </Box>
      )}

      {kind === 'gradient' && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            <input type="color" value={color} onChange={e => setColor(e.target.value)}
              style={{ width: 48, height: 40, border: 'none', cursor: 'pointer', borderRadius: 8 }} />
            <input type="color" value={color2} onChange={e => setColor2(e.target.value)}
              style={{ width: 48, height: 40, border: 'none', cursor: 'pointer', borderRadius: 8 }} />
          </Box>
          <Box sx={{ height: 24, borderRadius: 2, background: `linear-gradient(${angle}deg, ${color}, ${color2})`, mb: 1 }} />
          <Typography variant="caption">Angle: {angle}°</Typography>
          <Slider value={angle} min={0} max={360} onChange={(_, v) => setAngle(v)} size="small" />
        </Box>
      )}

      {kind === 'image' && (
        <Box
          sx={{ border: '2px dashed rgba(255,255,255,0.2)', borderRadius: 2, p: 2, textAlign: 'center', cursor: 'pointer', mb: 2 }}
          onClick={() => document.getElementById(inputId).click()}
        >
          <Typography variant="caption" color="text.secondary">
            Click to upload image (JPEG/PNG/WebP, max 5MB)
          </Typography>
          <input
            id={inputId}
            type="file" accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); }}
          />
          {uploading && <CircularProgress size={20} sx={{ mt: 1 }} />}
        </Box>
      )}

      {kind !== 'image' && (
        <Button size="small" variant="contained" onClick={() => onSave(buildValue())} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      )}
    </Box>
  );
}

function SortableNavItem({ id, label }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex', alignItems: 'center', gap: 2, p: 1.5,
        mb: 1, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 2,
        background: isDragging ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)',
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      <Box {...attributes} {...listeners} sx={{ cursor: 'grab', color: 'text.disabled', display: 'flex' }}>
        <DragHandleIcon />
      </Box>
      <Typography>{label}</Typography>
    </Box>
  );
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem('token');
  const decoded = token ? jwtDecode(token) : null;
  const userId = decoded?.id;
  const isAdmin = decoded?.isAdmin;

  // Resolve tab from ?tab=appearance etc.
  const queryTab = new URLSearchParams(location.search).get('tab');
  const [tabIndex, setTabIndex] = useState(Math.max(0, TAB_LABELS.indexOf(queryTab)));

  // Server state
  const [userData, setUserData] = useState(null);
  const [profileImage, setProfileImage] = useState('');
  const [loading, setLoading] = useState(true);

  // Tab 0 — Profile
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');

  // Tab 1 — Account
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountError, setAccountError] = useState('');

  // Tab 2 — Security (MFA)
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaStatus, setMfaStatus] = useState(null); // null | 'confirming' | 'enabled'
  const [mfaQr, setMfaQr] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaBackupCodes, setMfaBackupCodes] = useState([]);
  const [mfaError, setMfaError] = useState('');

  // Tab 3 — Privacy
  const [isHidden, setIsHidden] = useState(false);
  const [activityPrivacy, setActivityPrivacy] = useState('public');

  // Tab 4 — Appearance
  const [hiddenButtons, setHiddenButtons] = useState([]);
  const [viewModes, setViewModes] = useState(() =>
    Object.fromEntries(COLLECTIONS.map(c => [c.key, localStorage.getItem(lsViewKey(c.key)) || 'grid']))
  );
  const [sortOrders, setSortOrders] = useState(() =>
    Object.fromEntries(COLLECTIONS.map(c => [c.key, localStorage.getItem(lsSortKey(c.key)) || 'alpha_asc']))
  );
  const { preferences, updatePreferences, updateCollectionSettings, updateCollectionOrder } = useUserPreferences();
  const { featureFlags } = useFeatureFlags();
  // Only show collections whose feature flag is enabled (or have no flag — always-on)
  const enabledCollections = COLLECTIONS.filter(c => !c.featureFlag || featureFlags?.[c.featureFlag]);

  // My Schemas (custom collections) state
  const [mySchemas, setMySchemas] = useState([]);
  const [schemasLoading, setSchemasLoading] = useState(false);
  const [unpublishing, setUnpublishing] = useState(null);
  const [schemaDeleteConfirm, setSchemaDeleteConfirm] = useState(null);
  const [deletingSchema, setDeletingSchema] = useState(null);

  // Icon picker dialog state
  const [iconDialogOpen, setIconDialogOpen] = useState(false);
  const [iconDialogCollection, setIconDialogCollection] = useState(null);
  const [iconPickerTab, setIconPickerTab] = useState(0);
  const [iconSearch, setIconSearch] = useState('');
  const [iconUploading, setIconUploading] = useState(false);

  // Background state
  const [bgSaving, setBgSaving] = useState({});
  const [bgUploading, setBgUploading] = useState({});

  // Nav order state — full 10-key default so new collection types appear
  const ALL_COLLECTION_KEYS = ['games','consoles','lego','movies','music','books','board-games','trading-cards','manga','comics'];
  const mergeNavOrder = (stored) => {
    if (!stored || !stored.length) return ALL_COLLECTION_KEYS;
    const extra = ALL_COLLECTION_KEYS.filter(k => !stored.includes(k));
    return [...stored, ...extra];
  };
  const [navOrder, setNavOrder] = useState(() => mergeNavOrder(preferences?.collectionOrder));
  const [navOrderSaving, setNavOrderSaving] = useState(false);

  // Sync navOrder when preferences load (context may not be ready on first render)
  useEffect(() => {
    if (preferences?.collectionOrder) {
      setNavOrder(mergeNavOrder(preferences.collectionOrder));
    }
  }, [preferences?.collectionOrder]);

  // Tab 5 — Data
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmUsername, setDeleteConfirmUsername] = useState('');

  // Feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  useEffect(() => {
    if (!userId) { navigate('/login'); return; }
    axiosInstance.get(`/users/${userId}`)
      .then(r => {
        const u = r.data;
        setUserData(u);
        setUsername(u.username || '');
        setBio(u.bio || '');
        setEmail(u.email || '');
        setIsHidden(u.isHidden || false);
        setActivityPrivacy(u.privacy?.activity || 'public');
        setHiddenButtons(u.hiddenButtons || []);
        if (u.mfaEnabled) { setMfaEnabled(true); setMfaStatus('enabled'); }
        if (u.profileImage) {
          setProfileImage(
            u.profileImage.startsWith('/')
              ? `${axiosInstance.getBaseServerUrl()}${u.profileImage}`
              : u.profileImage
          );
        }
        setLoading(false);
      })
      .catch(() => { setLoading(false); navigate('/login'); });
  }, [userId, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // ── Avatar upload ──────────────────────────────────────────────────────────
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { showSnackbar('File size must be less than 5MB', 'error'); return; }
    const allowed = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowed.includes(file.type)) { showSnackbar('Only JPG, PNG, and GIF files are allowed', 'error'); return; }
    const fd = new FormData();
    fd.append('profileImage', file);
    try {
      const res = await axiosInstance.post(`/users/${userId}/profile-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data.profileImage;
      setProfileImage(url.startsWith('/') ? `${axiosInstance.getBaseServerUrl()}${url}` : url);
      showSnackbar('Avatar updated');
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to upload image', 'error');
    }
  };

  // ── Tab 0: Profile save ────────────────────────────────────────────────────
  const handleSaveProfile = async () => {
    if (bio.length > 200) return;
    try {
      await axiosInstance.put(`/users/${userId}`, { username, bio });
      showSnackbar('Profile saved');
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to save profile', 'error');
    }
  };

  // ── Tab 1: Account save ────────────────────────────────────────────────────
  const handleSaveAccount = async () => {
    setAccountError('');
    if (newPassword && newPassword !== confirmPassword) {
      setAccountError('Passwords do not match');
      return;
    }
    const body = { email };
    if (currentPassword) body.currentPassword = currentPassword;
    if (newPassword) body.password = newPassword;
    try {
      await axiosInstance.put(`/users/${userId}`, body);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showSnackbar('Account saved');
    } catch (err) {
      if (err.response?.status === 403) setAccountError('Incorrect current password');
      else setAccountError(err.response?.data?.error || 'Failed to save');
    }
  };

  // ── Tab 2: MFA handlers (moved from UserProfile.js) ───────────────────────
  const handleMfaEnable = async () => {
    try {
      setMfaError('');
      const res = await axiosInstance.post('/mfa/enable');
      setMfaQr(res.data.qrDataUri);
      setMfaSecret(res.data.secret);
      setMfaStatus('confirming');
    } catch (err) {
      setMfaError(err.response?.data?.message || 'Failed to start MFA setup.');
    }
  };

  const handleMfaConfirm = async () => {
    try {
      setMfaError('');
      const res = await axiosInstance.post('/mfa/confirm', { code: mfaCode });
      setMfaBackupCodes(res.data.backupCodes);
      setMfaEnabled(true);
      setMfaStatus('enabled');
      setMfaCode('');
    } catch (err) {
      setMfaError(err.response?.data?.message || 'Invalid code.');
    }
  };

  const handleMfaDisable = async () => {
    try {
      setMfaError('');
      await axiosInstance.post('/mfa/disable', { code: mfaCode });
      setMfaEnabled(false);
      setMfaStatus(null);
      setMfaCode('');
      setMfaBackupCodes([]);
    } catch (err) {
      setMfaError(err.response?.data?.message || 'Invalid code.');
    }
  };

  // ── Tab 3: Privacy save ────────────────────────────────────────────────────
  const handleSavePrivacy = async () => {
    try {
      await axiosInstance.put(`/users/${userId}`, {
        isHidden,
        'privacy.activity': activityPrivacy,
      });
      showSnackbar('Privacy settings saved');
    } catch {
      showSnackbar('Failed to save privacy settings', 'error');
    }
  };

  // ── Tab 4: Appearance handlers (from SettingsCustomisationPage) ────────────
  const handleToggleHidden = async (hiddenKey) => {
    const next = hiddenButtons.includes(hiddenKey)
      ? hiddenButtons.filter(b => b !== hiddenKey)
      : [...hiddenButtons, hiddenKey];
    setHiddenButtons(next);
    try {
      await axiosInstance.put(`/users/${userId}`, { hiddenButtons: next });
      showSnackbar('Collection visibility saved');
    } catch {
      showSnackbar('Failed to save', 'error');
    }
  };

  const handleViewModeChange = (key, value) => {
    setViewModes(prev => ({ ...prev, [key]: value }));
    localStorage.setItem(lsViewKey(key), value);
    showSnackbar('Default view saved');
  };

  const handleSortOrderChange = (key, value) => {
    setSortOrders(prev => ({ ...prev, [key]: value }));
    localStorage.setItem(lsSortKey(key), value);
    showSnackbar('Default sort saved');
  };

  // ── Icon + background + nav order handlers ────────────────────────────────
  const handleIconPresetSelect = async (collection, iconName) => {
    await updateCollectionSettings(collection, { icon: { source: 'mui', value: iconName } });
    setIconDialogOpen(false);
  };

  const handleIconReset = async (collection) => {
    await updateCollectionSettings(collection, { icon: { source: 'default', value: null } });
    setIconDialogOpen(false);
  };

  const handleIconUpload = async (collection, file) => {
    setIconUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axiosInstance.post(
        `/users/me/assets?kind=icon&collection=${collection}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      await updateCollectionSettings(collection, { icon: { source: 'custom', value: res.data.assetKey } });
      setIconDialogOpen(false);
    } catch (err) {
      showSnackbar('Icon upload failed', 'error');
    } finally {
      setIconUploading(false);
    }
  };

  const handleBgSave = async (collection, kind, value) => {
    const saveKey = `${collection}-${kind}`;
    setBgSaving(prev => ({ ...prev, [saveKey]: true }));
    try {
      const patch = kind === 'card' ? { cardBackground: value } : { pageBackground: value };
      await updateCollectionSettings(collection, patch);
      showSnackbar('Background saved');
    } catch {
      showSnackbar('Save failed', 'error');
    } finally {
      setBgSaving(prev => ({ ...prev, [saveKey]: false }));
    }
  };

  const handleBgImageUpload = async (collection, kind, file) => {
    const uploadKey = `${collection}-${kind}`;
    setBgUploading(prev => ({ ...prev, [uploadKey]: true }));
    try {
      const apiKind = kind === 'card' ? 'card-bg' : 'page-bg';
      const formData = new FormData();
      formData.append('file', file);
      const res = await axiosInstance.post(
        `/users/me/assets?kind=${apiKind}&collection=${collection}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const patch = kind === 'card'
        ? { cardBackground: { kind: 'image', assetKey: res.data.assetKey } }
        : { pageBackground: { kind: 'image', assetKey: res.data.assetKey } };
      await updateCollectionSettings(collection, patch);
      showSnackbar('Background saved');
    } catch {
      showSnackbar('Upload failed', 'error');
    } finally {
      setBgUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleNavDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setNavOrder(prev => {
        const oldIdx = prev.indexOf(active.id);
        const newIdx = prev.indexOf(over.id);
        return arrayMove(prev, oldIdx, newIdx);
      });
    }
  };

  const handleNavOrderSave = async () => {
    setNavOrderSaving(true);
    try {
      await updateCollectionOrder(navOrder);
      showSnackbar('Nav order saved');
    } catch {
      showSnackbar('Save failed', 'error');
    } finally {
      setNavOrderSaving(false);
    }
  };

  // ── Tab 6: My Schemas ─────────────────────────────────────────────────────
  useEffect(() => {
    if (tabIndex === 6 && featureFlags?.enableCustomCollections) {
      setSchemasLoading(true);
      axiosInstance.get('/custom-schemas/mine')
        .then(r => setMySchemas(r.data))
        .catch(() => {})
        .finally(() => setSchemasLoading(false));
    }
  }, [tabIndex, featureFlags?.enableCustomCollections]);

  const handleUnpublish = async (schemaId) => {
    setUnpublishing(schemaId);
    try {
      await axiosInstance.post(`/custom-schemas/${schemaId}/unpublish`);
      setMySchemas(prev => prev.map(s => s._id === schemaId ? { ...s, isPublished: false, isRemovedFromLibrary: true } : s));
    } catch {
      showSnackbar('Failed to unpublish', 'error');
    } finally {
      setUnpublishing(null);
    }
  };

  const handleDeleteSchema = async () => {
    if (!schemaDeleteConfirm) return;
    setDeletingSchema(schemaDeleteConfirm._id);
    try {
      await axiosInstance.delete(`/custom-schemas/${schemaDeleteConfirm._id}`);
      setMySchemas(prev => prev.filter(s => s._id !== schemaDeleteConfirm._id));
      setSchemaDeleteConfirm(null);
    } catch {
      showSnackbar('Failed to delete schema', 'error');
    } finally {
      setDeletingSchema(null);
    }
  };

  // ── Tab 5: Delete account ──────────────────────────────────────────────────
  const handleDeleteAccount = async () => {
    if (deleteConfirmUsername !== userData?.username) return;
    try {
      await axiosInstance.delete(`/users/${userId}`);
      localStorage.removeItem('token');
      navigate('/register');
    } catch {
      showSnackbar('Failed to delete account', 'error');
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <>
        <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>Profile</Typography>

        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Profile" />
          <Tab label="Account" />
          <Tab label="Security" />
          <Tab label="Privacy" />
          <Tab label="Appearance" />
          <Tab label="Data" />
          {featureFlags?.enableCustomCollections && <Tab label="My Schemas" />}
          {featureFlags?.enableStreaks && <Tab label="Activity" />}
        </Tabs>

        {/* ── Tab 0: Profile ─────────────────────────────────────────────── */}
        {tabIndex === 0 && (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar src={profileImage} sx={{ width: 100, height: 100 }} />
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="avatar-upload"
                  type="file"
                  onChange={handleImageUpload}
                />
                <label htmlFor="avatar-upload">
                  <IconButton
                    component="span"
                    color="primary"
                    aria-label="upload picture"
                    sx={{
                      position: 'absolute', bottom: 0, right: -24,
                      bgcolor: 'background.paper', boxShadow: 1,
                      '&:hover': { bgcolor: 'background.paper', boxShadow: 2 },
                    }}
                  >
                    <PhotoCamera />
                  </IconButton>
                </label>
              </Box>
            </Box>

            <TextField
              fullWidth label="Username" value={username}
              onChange={e => setUsername(e.target.value)} sx={{ mb: 2 }}
            />
            <TextField
              fullWidth multiline rows={3} label="Bio"
              value={bio} onChange={e => setBio(e.target.value)}
              inputProps={{ maxLength: 201 }}
              helperText={`${bio.length}/200`}
              error={bio.length > 200}
              sx={{ mb: 2 }}
            />
            {userData?.createdAt && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Joined {new Date(userData.createdAt).toLocaleDateString()}
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleSaveProfile}
              disabled={bio.length > 200}
            >
              Save
            </Button>
          </Box>
        )}

        {/* ── Tab 1: Account ─────────────────────────────────────────────── */}
        {tabIndex === 1 && (
          <Box>
            {accountError && <Alert severity="error" sx={{ mb: 2 }}>{accountError}</Alert>}
            <TextField
              fullWidth label="Email" type="email" value={email}
              onChange={e => setEmail(e.target.value)} sx={{ mb: 2 }}
            />
            {(email !== (userData?.email || '') || newPassword) && (
              <TextField
                fullWidth label="Current Password" type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                required sx={{ mb: 2 }}
                helperText="Required to change email or password"
              />
            )}
            <TextField
              fullWidth label="New Password" type="password" value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              helperText="Leave blank to keep current password"
              sx={{ mb: 2 }}
            />
            {newPassword && (
              <TextField
                fullWidth label="Confirm New Password" type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
            )}
            <Button variant="contained" onClick={handleSaveAccount}>Save</Button>
          </Box>
        )}

        {/* ── Tab 2: Security (MFA) ───────────────────────────────────────── */}
        {tabIndex === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>Two-Factor Authentication</Typography>
            {mfaError && <Alert severity="error" sx={{ mb: 2 }}>{mfaError}</Alert>}

            {!mfaEnabled && mfaStatus === null && (
              <Button variant="outlined" onClick={handleMfaEnable}>
                Enable Two-Factor Authentication
              </Button>
            )}

            {mfaStatus === 'confirming' && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Scan this QR code with your authenticator app, then enter the 6-digit code to activate.
                </Typography>
                <Box component="img" src={mfaQr} alt="MFA QR Code" sx={{ display: 'block', mb: 2, width: 200 }} />
                <Typography variant="caption" sx={{ mb: 2, display: 'block' }}>
                  Manual entry key: <strong>{mfaSecret}</strong>
                </Typography>
                <TextField
                  label="Verification Code" value={mfaCode}
                  onChange={e => setMfaCode(e.target.value)}
                  inputProps={{ maxLength: 6 }} sx={{ mb: 2 }} size="small"
                />
                <Button variant="contained" onClick={handleMfaConfirm} sx={{ ml: 1 }}>
                  Activate MFA
                </Button>
              </Box>
            )}

            {mfaEnabled && mfaBackupCodes.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Save these backup codes.</strong> They will not be shown again.
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  {mfaBackupCodes.map(c => <li key={c}><code>{c}</code></li>)}
                </Box>
              </Alert>
            )}

            {mfaEnabled && (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>MFA is active on your account.</Alert>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Enter your authenticator code or a backup code to disable MFA.
                </Typography>
                <TextField
                  label="Code" value={mfaCode}
                  onChange={e => setMfaCode(e.target.value)}
                  size="small" sx={{ mb: 1 }}
                />
                <Button variant="outlined" color="error" onClick={handleMfaDisable} sx={{ ml: 1 }}>
                  Disable MFA
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* ── Tab 3: Privacy ─────────────────────────────────────────────── */}
        {tabIndex === 3 && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHidden}
                  onChange={e => setIsHidden(e.target.checked)}
                />
              }
              label="Hide my profile from Community page"
              sx={{ mb: 2, display: 'block' }}
            />
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Activity Feed Visibility</InputLabel>
              <Select
                label="Activity Feed Visibility"
                value={activityPrivacy}
                onChange={e => setActivityPrivacy(e.target.value)}
              >
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="followers">Followers only</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleSavePrivacy}>Save</Button>
          </Box>
        )}

        {/* ── Tab 4: Appearance ──────────────────────────────────────────── */}
        {tabIndex === 4 && (
          <Box>
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Appearance</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Changes apply instantly and are saved to your account.
              </Typography>

              {/* Theme swatches */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Colour Theme</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {Object.entries(THEMES).map(([key, palette]) => (
                  <Tooltip key={key} title={THEME_LABELS[key]}>
                    <Box
                      onClick={() => { updatePreferences({ theme: key, accentColor: null }); showSnackbar('Theme saved'); }}
                      role="button" tabIndex={0}
                      aria-label={THEME_LABELS[key]}
                      aria-pressed={preferences.theme === key && !preferences.accentColor}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); updatePreferences({ theme: key, accentColor: null }); showSnackbar('Theme saved'); } }}
                      sx={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                        cursor: 'pointer',
                        border: preferences.theme === key && !preferences.accentColor ? '3px solid #fff' : '3px solid transparent',
                        boxShadow: preferences.theme === key && !preferences.accentColor ? `0 0 0 2px ${palette.primary}` : 'none',
                        transition: 'box-shadow 0.2s',
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>

              {/* Custom accent */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, mt: 1 }}>
                <Typography variant="body2" color="text.secondary">Custom accent:</Typography>
                <Box
                  component="input" type="color"
                  value={preferences.accentColor || (THEMES[preferences.theme]?.primary ?? '#7C8CFF')}
                  onChange={e => { updatePreferences({ accentColor: e.target.value }); showSnackbar('Accent colour saved'); }}
                  sx={{ width: 36, height: 36, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', cursor: 'pointer', padding: 0, backgroundColor: 'transparent', '&::-webkit-color-swatch-wrapper': { padding: 0 }, '&::-webkit-color-swatch': { borderRadius: '50%', border: 'none' } }}
                />
                {preferences.accentColor && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => { updatePreferences({ accentColor: null }); showSnackbar('Reset to theme colour'); }}
                    role="button" tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); updatePreferences({ accentColor: null }); showSnackbar('Reset to theme colour'); } }}
                  >
                    Reset to theme
                  </Typography>
                )}
              </Box>

              {/* Font picker */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Font</Typography>
              <ToggleButtonGroup
                value={preferences.fontCategory} exclusive size="small" sx={{ mb: 1.5 }}
                onChange={(_, val) => {
                  if (!val) return;
                  const firstFont = FONT_CATEGORIES[val]?.fonts[0] || 'Inter';
                  updatePreferences({ fontCategory: val, font: firstFont });
                  showSnackbar('Font saved');
                }}
              >
                {Object.entries(FONT_CATEGORIES).map(([key, cat]) => (
                  <ToggleButton key={key} value={key}>{cat.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {(FONT_CATEGORIES[preferences.fontCategory]?.fonts || []).map(font => (
                  <Box
                    key={font}
                    onClick={() => { updatePreferences({ font }); showSnackbar('Font saved'); }}
                    role="button" tabIndex={0} aria-label={font} aria-pressed={preferences.font === font}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); updatePreferences({ font }); showSnackbar('Font saved'); } }}
                    sx={{
                      px: 2, py: 0.75, borderRadius: 2,
                      fontFamily: `'${font}', sans-serif`, fontSize: '0.9rem', cursor: 'pointer',
                      border: '2px solid',
                      borderColor: preferences.font === font ? 'primary.main' : 'rgba(255,255,255,0.15)',
                      bgcolor: preferences.font === font ? 'rgba(124,140,255,0.12)' : 'rgba(255,255,255,0.04)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {font}
                  </Box>
                ))}
              </Box>

              {/* Icon style */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Icon Style</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {['filled', 'outlined', 'rounded'].map(style => (
                  <Box
                    key={style}
                    onClick={() => { updatePreferences({ iconStyle: style }); showSnackbar('Icon style saved'); }}
                    role="button" tabIndex={0} aria-label={style} aria-pressed={preferences.iconStyle === style}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); updatePreferences({ iconStyle: style }); showSnackbar('Icon style saved'); } }}
                    sx={{
                      flex: 1, p: 2, borderRadius: 2, border: '2px solid',
                      borderColor: preferences.iconStyle === style ? 'primary.main' : 'rgba(255,255,255,0.12)',
                      bgcolor: preferences.iconStyle === style ? 'rgba(124,140,255,0.08)' : 'rgba(255,255,255,0.03)',
                      cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                      <AppIcon name="Settings" fontSize="small" sx={{ color: preferences.iconStyle === style ? 'primary.main' : 'text.secondary' }} />
                      <AppIcon name="Person" fontSize="small" sx={{ color: preferences.iconStyle === style ? 'primary.main' : 'text.secondary' }} />
                      <AppIcon name="Star" fontSize="small" sx={{ color: preferences.iconStyle === style ? 'primary.main' : 'text.secondary' }} />
                    </Box>
                    <Typography variant="caption" sx={{ textTransform: 'capitalize', color: 'text.secondary' }}>
                      {style}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>

            {/* Collection Visibility */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Collection Visibility</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Hidden collections are removed from the navigation bar. You can still access them directly via their URL.
              </Typography>
              <Grid container spacing={0}>
                {enabledCollections.map(c => (
                  <Grid item xs={12} sm={6} key={c.key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={hiddenButtons.includes(c.hiddenKey)}
                          onChange={() => handleToggleHidden(c.hiddenKey)}
                        />
                      }
                      label={`Hide ${c.label} Collection`}
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Default View Mode */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Default View Mode</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each collection opens in this view mode by default.
              </Typography>
              <Grid container spacing={2}>
                {enabledCollections.map(c => (
                  <Grid item xs={12} sm={6} md={4} key={c.key}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{c.label}</InputLabel>
                      <Select
                        label={c.label}
                        value={viewModes[c.key]}
                        onChange={e => handleViewModeChange(c.key, e.target.value)}
                      >
                        {VIEW_MODE_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Default Sort Order */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Default Sort Order</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each collection loads with this sort order by default.
              </Typography>
              <Grid container spacing={2}>
                {enabledCollections.map(c => (
                  <Grid item xs={12} sm={6} md={4} key={c.key}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{c.label}</InputLabel>
                      <Select
                        label={c.label}
                        value={sortOrders[c.key]}
                        onChange={e => handleSortOrderChange(c.key, e.target.value)}
                      >
                        {SORT_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Collection Icons */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Collection Icons</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose a preset icon name or upload a custom image for each collection.
              </Typography>
              <Grid container spacing={2}>
                {enabledCollections.filter(c => !hiddenButtons.includes(c.hiddenKey)).map(c => {
                  const cs = preferences?.collectionSettings?.[c.key];
                  const hasCustomIcon = cs?.icon?.source === 'custom' && cs?.icon?.value;
                  const hasMuiIcon = cs?.icon?.source === 'mui' && cs?.icon?.value;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={c.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 2 }}>
                        <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {hasCustomIcon ? (
                            <img
                              src={`/uploads/${cs.icon.value}`}
                              alt={c.label}
                              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 60 }}>
                              {hasMuiIcon ? cs.icon.value : 'Default'}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ flex: 1 }}>{c.label}</Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setIconDialogCollection(c.key);
                            setIconPickerTab(0);
                            setIconSearch('');
                            setIconDialogOpen(true);
                          }}
                        >
                          Change
                        </Button>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            {/* Icon Picker Dialog */}
            <Dialog open={iconDialogOpen} onClose={() => setIconDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Change Icon — {COLLECTIONS.find(c => c.key === iconDialogCollection)?.label}</DialogTitle>
              <DialogContent>
                <Tabs value={iconPickerTab} onChange={(_, v) => setIconPickerTab(v)} sx={{ mb: 2 }}>
                  <Tab label="Preset" />
                  <Tab label="Custom Upload" />
                </Tabs>
                {iconPickerTab === 0 && (
                  <>
                    <TextField
                      size="small" fullWidth placeholder="Search icons…" value={iconSearch}
                      onChange={e => setIconSearch(e.target.value)} sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 300, overflowY: 'auto' }}>
                      {CURATED_ICONS.filter(n => n.toLowerCase().includes(iconSearch.toLowerCase())).map(name => (
                        <Chip
                          key={name}
                          label={name.replace(/([A-Z])/g, ' $1').trim()}
                          size="small"
                          clickable
                          onClick={() => handleIconPresetSelect(iconDialogCollection, name)}
                          variant={preferences?.collectionSettings?.[iconDialogCollection]?.icon?.value === name ? 'filled' : 'outlined'}
                          color={preferences?.collectionSettings?.[iconDialogCollection]?.icon?.value === name ? 'primary' : 'default'}
                        />
                      ))}
                    </Box>
                  </>
                )}
                {iconPickerTab === 1 && (
                  <Box
                    sx={{ border: '2px dashed rgba(255,255,255,0.2)', borderRadius: 2, p: 4, textAlign: 'center', cursor: 'pointer' }}
                    onClick={() => document.getElementById('icon-upload-input').click()}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Click to upload (JPEG, PNG, WebP — max 2MB). Will be cropped to 128×128px.
                    </Typography>
                    <input
                      id="icon-upload-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) handleIconUpload(iconDialogCollection, file);
                      }}
                    />
                    {iconUploading && <CircularProgress size={24} sx={{ mt: 2 }} />}
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  color="warning"
                  onClick={() => handleIconReset(iconDialogCollection)}
                >
                  Reset to Default
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button onClick={() => setIconDialogOpen(false)}>Cancel</Button>
              </DialogActions>
            </Dialog>

            {/* Collection Backgrounds */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Collection Backgrounds</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set a card background and page background for each collection.
              </Typography>
              {enabledCollections.filter(c => !hiddenButtons.includes(c.hiddenKey)).map(c => (
                <Accordion key={c.key} disableGutters sx={{ mb: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{c.label}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <BackgroundSubSection
                      label="Card Background"
                      value={preferences?.collectionSettings?.[c.key]?.cardBackground}
                      onSave={v => handleBgSave(c.key, 'card', v)}
                      saving={!!bgSaving[`${c.key}-card`]}
                      onUpload={f => handleBgImageUpload(c.key, 'card', f)}
                      uploading={!!bgUploading[`${c.key}-card`]}
                    />
                    <Divider sx={{ my: 2 }} />
                    <BackgroundSubSection
                      label="Page Background"
                      value={preferences?.collectionSettings?.[c.key]?.pageBackground}
                      onSave={v => handleBgSave(c.key, 'page', v)}
                      saving={!!bgSaving[`${c.key}-page`]}
                      onUpload={f => handleBgImageUpload(c.key, 'page', f)}
                      uploading={!!bgUploading[`${c.key}-page`]}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}
            </Paper>

            {/* Navigation Order */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Navigation Order</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Drag to reorder the collection tabs in the top navigation bar.
              </Typography>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleNavDragEnd}>
                <SortableContext items={navOrder} strategy={verticalListSortingStrategy}>
                  {navOrder.filter(key => {
                    const c = COLLECTIONS.find(col => col.key === key);
                    if (!c) return false;
                    if (c.featureFlag && !featureFlags?.[c.featureFlag]) return false;
                    return !hiddenButtons.includes(c.hiddenKey);
                  }).map(key => {
                    const label = COLLECTIONS.find(c => c.key === key)?.label ?? key;
                    return <SortableNavItem key={key} id={key} label={label} />;
                  })}
                </SortableContext>
              </DndContext>
              <Button variant="contained" onClick={handleNavOrderSave} disabled={navOrderSaving} sx={{ mt: 1 }}>
                {navOrderSaving ? 'Saving…' : 'Save Order'}
              </Button>
            </Paper>
          </Box>
        )}

        {/* ── Tab 5: Data ────────────────────────────────────────────────── */}
        {tabIndex === 5 && (
          <Box>
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>Data Management</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Import or export your collections and wishlists. Back up your data or transfer it to another account.
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<ImportExportIcon />}
                onClick={() => navigate('/import-export')}
              >
                Import / Export Data
              </Button>
            </Paper>

            <Paper elevation={2} sx={{ p: 3, border: '1px solid', borderColor: 'error.main' }}>
              <Typography variant="h6" color="error" gutterBottom>Danger Zone</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Permanently delete your account and all associated data. This cannot be undone.
              </Typography>
              <Button variant="outlined" color="error" onClick={() => setDeleteDialogOpen(true)}>
                Delete Account
              </Button>
            </Paper>

            <Dialog
              open={deleteDialogOpen}
              onClose={() => { setDeleteDialogOpen(false); setDeleteConfirmUsername(''); }}
            >
              <DialogTitle>Delete Account</DialogTitle>
              <DialogContent>
                <Typography sx={{ mb: 2 }}>
                  This action is permanent and cannot be undone. Type <strong>{userData?.username}</strong> to confirm.
                </Typography>
                <TextField
                  fullWidth label="Username" value={deleteConfirmUsername}
                  onChange={e => setDeleteConfirmUsername(e.target.value)}
                  autoFocus
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmUsername(''); }}>
                  Cancel
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  disabled={deleteConfirmUsername !== userData?.username}
                  onClick={handleDeleteAccount}
                >
                  Delete My Account
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {/* ── Tab 6: My Schemas ──────────────────────────────────────────── */}
        {tabIndex === 6 && featureFlags?.enableCustomCollections && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">My Schemas</Typography>
              <Button variant="contained" onClick={() => navigate('/custom-schemas/new')}>
                Create New Schema
              </Button>
            </Box>
            <Button variant="outlined" sx={{ mb: 3 }} onClick={() => navigate('/schema-library')}>
              Browse Schema Library
            </Button>
            {schemasLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
            ) : mySchemas.length === 0 ? (
              <Typography color="text.secondary">You haven't created any schemas yet.</Typography>
            ) : (
              <Grid container spacing={2}>
                {mySchemas.map(schema => (
                  <Grid item xs={12} sm={6} key={schema._id}>
                    <Paper elevation={2} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      {schema.icon && <Typography variant="h5">{schema.icon}</Typography>}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>{schema.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip size="small" label={`v${schema.version}`} />
                          {schema.isPublished && !schema.isRemovedFromLibrary && (
                            <Chip size="small" label="Published" color="success" />
                          )}
                          {schema.isRemovedFromLibrary && (
                            <Chip size="small" label="Removed from library" color="warning" />
                          )}
                          {!schema.isPublished && !schema.isRemovedFromLibrary && (
                            <Chip size="small" label="Private" />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        <Button size="small" variant="outlined" onClick={() => navigate(`/custom-schemas/${schema._id}/edit`)}>
                          Edit
                        </Button>
                        {schema.isPublished && !schema.isRemovedFromLibrary && (
                          <Button
                            size="small" color="warning"
                            disabled={unpublishing === schema._id}
                            onClick={() => handleUnpublish(schema._id)}
                          >
                            {unpublishing === schema._id ? <CircularProgress size={16} /> : 'Unpublish'}
                          </Button>
                        )}
                        <Button
                          size="small" color="error"
                          onClick={() => setSchemaDeleteConfirm(schema)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Delete schema confirmation dialog */}
            <Dialog open={Boolean(schemaDeleteConfirm)} onClose={() => setSchemaDeleteConfirm(null)}>
              <DialogTitle>Delete Schema</DialogTitle>
              <DialogContent>
                <Typography>
                  Delete <strong>{schemaDeleteConfirm?.name}</strong>? This will permanently delete the schema and all its items. Adopters will lose access. This cannot be undone.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSchemaDeleteConfirm(null)}>Cancel</Button>
                <Button
                  color="error" variant="contained"
                  disabled={deletingSchema === schemaDeleteConfirm?._id}
                  onClick={handleDeleteSchema}
                >
                  {deletingSchema ? <CircularProgress size={20} /> : 'Delete'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {/* ── Activity Tab ──────────────────────────────────────────────── */}
        {tabIndex === TAB_LABELS.indexOf('activity') && featureFlags?.enableStreaks && (
          <ActivityTab />
        )}

      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
